import { withAlpha } from "@/lib/color";
import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trPositionShort } from "@/lib/pesTranslations";

// Ordinamento per RUOLO (non per VG) nei menu di selezione giocatore
const POS_RANK = {
    GK: 0, CB: 1, LCB: 1, RCB: 1, LB: 2, RB: 2, SB: 2, LWB: 2, RWB: 2,
    DMF: 3, CMF: 4, LMF: 5, RMF: 6, AMF: 7, LWF: 8, RWF: 9, SS: 10, CF: 11,
};
const sortByRole = (arr) =>
    [...(arr || [])].sort(
        (a, b) =>
            (POS_RANK[a.position] ?? 99) - (POS_RANK[b.position] ?? 99) ||
            (a.name || "").localeCompare(b.name || ""),
    );

const BOOKING_COLORS = {
    yellow: { bg: "rgb(var(--tc-e8ff00))", label: "🟨 AMM" },
    red_direct: { bg: "rgb(var(--tc-ff4d4d))", label: "🟥 DIR" },
    red_double: { bg: "rgb(var(--tc-ff7a1a))", label: "🟥 2A" },
};

/**
 * TabellinoModal — form for entering/editing match result.
 *
 * Props:
 *  - matchId: string
 *  - onClose: () => void
 *  - onSaved: (newMatch) => void
 */
export default function TabellinoModal({ matchId, onClose, onSaved }) {
    const { user } = useAuth();
    const [match, setMatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [homeScore, setHomeScore] = useState(0);
    const [awayScore, setAwayScore] = useState(0);
    const [scorers, setScorers] = useState([]); // {player_pes_id, team_id, goals}
    const [ownGoals, setOwnGoals] = useState([]); // {team_id (conceding), player_pes_id?}
    const [mvpId, setMvpId] = useState(""); // "team_id:player_pes_id"
    const [homeGk, setHomeGk] = useState({ pes_id: "", rating: 6.0 });
    const [awayGk, setAwayGk] = useState({ pes_id: "", rating: 6.0 });
    const [bookings, setBookings] = useState([]); // {player_pes_id, team_id, type}
    const [penaltyWinner, setPenaltyWinner] = useState(""); // "" | "home" | "away" — solo per la bella
    const [suspended, setSuspended] = useState([]); // [{pes_id, name, team_id, reason}]
    const [warningsLight, setWarningsLight] = useState([]); // [{pes_id, name, team_id, yellows}]
    const [injured, setInjured] = useState([]); // [{pes_id, name, team_id, weeks_remaining}]

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/matches/${matchId}`);
                setMatch(data);
                // Injuries come bundled with the match payload
                setInjured(data.injured || []);
                // Hydrate form from existing tabellino if any
                if (data.status === "played") {
                    setHomeScore(data.home_score ?? 0);
                    setAwayScore(data.away_score ?? 0);
                    setScorers((data.scorers || []).map((s) => ({
                        player_pes_id: String(s.player_pes_id),
                        team_id: s.team_id,
                        goals: s.goals,
                    })));
                    if (data.mvp) setMvpId(`${data.mvp.team_id}:${data.mvp.player_pes_id}`);
                    if (data.own_goals) setOwnGoals((data.own_goals || []).map((o) => ({
                        team_id: o.team_id,
                        player_pes_id: o.player_pes_id ? String(o.player_pes_id) : "",
                    })));
                    if (data.home_gk) setHomeGk({ pes_id: String(data.home_gk.player_pes_id), rating: data.home_gk.rating });
                    if (data.away_gk) setAwayGk({ pes_id: String(data.away_gk.player_pes_id), rating: data.away_gk.rating });
                    if (data.bookings) setBookings((data.bookings || []).map((b) => ({
                        player_pes_id: String(b.player_pes_id),
                        team_id: b.team_id,
                        type: b.type,
                    })));
                    if (data.penalty_winner) setPenaltyWinner(data.penalty_winner);
                } else {
                    // Pre-pick highest VG GK from each roster
                    const hgk = (data.home_team.roster || []).find((p) => p.position === "GK");
                    const agk = (data.away_team.roster || []).find((p) => p.position === "GK");
                    if (hgk) setHomeGk({ pes_id: String(hgk.pes_id), rating: 6.0 });
                    if (agk) setAwayGk({ pes_id: String(agk.pes_id), rating: 6.0 });
                }
            } catch (e) {
                setError(formatApiErrorDetail(e.response?.data?.detail));
            } finally {
                setLoading(false);
            }
        })();
    }, [matchId]);

    // Load disciplinary state for the relevant competition (so we can warn about suspended players)
    useEffect(() => {
        if (!match) return;
        (async () => {
            try {
                let url = null;
                if (match.kind === "league" && match.division_code) {
                    url = `/divisions/${match.division_code}/disciplinary?exclude_match_id=${matchId}`;
                } else if (match.kind === "cup" && match.cup_slug) {
                    url = `/cups/${match.cup_slug}/disciplinary?exclude_match_id=${matchId}`;
                }
                if (!url) return;
                const { data } = await api.get(url);
                // Filter only players from one of the two teams in this match
                const teams = new Set([match.home_team_id, match.away_team_id]);
                const susp = (data?.suspended || []).filter((p) => teams.has(p.team?.id));
                const warns = (data?.warnings || []).filter((p) => teams.has(p.team?.id));
                setSuspended(susp);
                setWarningsLight(warns);
            } catch (e) {
                // silent
            }
        })();
    }, [match]);

    const suspendedInPicker = (pid) => {
        const hit = suspended.find((p) => String(p.pes_id) === String(pid));
        return hit ? hit.suspension_reason : null;
    };
    const injuredInPicker = (pid) => {
        const hit = injured.find((p) => String(p.pes_id) === String(pid));
        if (!hit) return null;
        if (hit.start_giornata && hit.end_giornata) {
            const range = hit.start_giornata === hit.end_giornata
                ? `G${hit.start_giornata}`
                : `G${hit.start_giornata}–G${hit.end_giornata}`;
            return `infortunato — salta le giornate ${range}`;
        }
        const n = hit.weeks_remaining;
        return `infortunato — salta ${n} ${n === 1 ? "partita" : "partite"}`;
    };
    // Combined warning string for a player (susp or injured)
    const playerBlockReason = (pid) => suspendedInPicker(pid) || injuredInPicker(pid);

    // Check permission
    const isMine = useMemo(() => {
        if (!match || !user) return false;
        const tid = String(user.team_id || "");
        return Boolean(tid && (tid === match.home_team_id || tid === match.away_team_id));
    }, [match, user]);

    const canEdit = useMemo(() => {
        if (!match || !user) return false;
        if (user.role === "admin") return true; // l'admin può modificare sempre
        if (!isMine) return false;
        // LOCK: tabellino già inserito → il presidente non può più modificarlo (solo admin).
        if (match.status === "played") return false;
        // Finestra di inserimento: solo quando aperta. Se non ci sono date, consenti (fallback).
        if (match.deadline == null && match.date_from == null) return true;
        return Boolean(match.window_open);
    }, [match, user, isMine]);

    const lockReason = useMemo(() => {
        if (canEdit || user?.role === "admin" || !isMine || !match) return null;
        if (match.status === "played") return "Tabellino già inserito: ora solo l'admin può modificarlo. Per correzioni contatta l'admin.";
        if (match.date_from && match.window_open === false && match.deadline) {
            const today = new Date().toISOString().slice(0, 10);
            if (today < match.date_from) return `La giornata non è ancora iniziata: il tabellino si attiva il ${match.date_from}.`;
            if (today > match.deadline) return "Termine scaduto: ora il risultato lo gestisce l'admin (tavolino). Per contestare, fai ricorso su Telegram.";
        }
        return null;
    }, [canEdit, isMine, match, user]);

    const homeRoster = match?.home_team?.roster || [];
    const awayRoster = match?.away_team?.roster || [];
    const allPlayers = useMemo(() => {
        const arr = [];
        for (const p of homeRoster) arr.push({ ...p, team_id: match?.home_team_id, side: "home" });
        for (const p of awayRoster) arr.push({ ...p, team_id: match?.away_team_id, side: "away" });
        return arr;
    }, [homeRoster, awayRoster, match]);

    const homeGks = homeRoster.filter((p) => p.position === "GK");
    const awayGks = awayRoster.filter((p) => p.position === "GK");
    const homeFieldPlayers = sortByRole(homeRoster.filter((p) => p.position !== "GK"));
    const awayFieldPlayers = sortByRole(awayRoster.filter((p) => p.position !== "GK"));

    const bumpScore = (team_id, delta) => {
        if (delta === 0) return;
        if (team_id === match?.home_team_id) setHomeScore((s) => Math.max(0, Number(s || 0) + delta));
        else setAwayScore((s) => Math.max(0, Number(s || 0) + delta));
    };
    const addScorer = (team_id) => {
        setScorers((arr) => [...arr, { player_pes_id: "", team_id, goals: 1 }]);
        // Auto-incrementa il punteggio della squadra (1 gol per riga aggiunta)
        bumpScore(team_id, 1);
    };
    const updateScorer = (idx, patch) => {
        // Se cambia il numero di gol, aggiorna il punteggio della squadra in automatico
        if (patch.goals !== undefined) {
            const row = scorers[idx];
            const delta = Number(patch.goals || 0) - Number(row?.goals || 0);
            bumpScore(row?.team_id, delta);
        }
        setScorers((arr) => arr.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    };
    const removeScorer = (idx) => {
        // Scala dal punteggio i gol della riga rimossa
        const row = scorers[idx];
        if (row) bumpScore(row.team_id, -Number(row.goals || 0));
        setScorers((arr) => arr.filter((_, i) => i !== idx));
    };

    // Autogol: team_id = squadra del giocatore (chi subisce); il gol va all'avversaria.
    const benefOf = (concedingTeamId) =>
        concedingTeamId === match?.home_team_id ? match?.away_team_id : match?.home_team_id;
    const addOwnGoal = (concedingTeamId) => {
        setOwnGoals((arr) => [...arr, { team_id: concedingTeamId, player_pes_id: "" }]);
        bumpScore(benefOf(concedingTeamId), 1);
    };
    const updateOwnGoal = (idx, patch) =>
        setOwnGoals((arr) => arr.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
    const removeOwnGoal = (idx) => {
        const row = ownGoals[idx];
        if (row) bumpScore(benefOf(row.team_id), -1);
        setOwnGoals((arr) => arr.filter((_, i) => i !== idx));
    };

    const addBooking = (team_id, type) => {
        setBookings((arr) => [...arr, { player_pes_id: "", team_id, type }]);
    };
    const updateBooking = (idx, patch) => {
        setBookings((arr) => arr.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
    };
    const removeBooking = (idx) => {
        setBookings((arr) => arr.filter((_, i) => i !== idx));
    };

    const homeScorerGoals = scorers
        .filter((s) => s.team_id === match?.home_team_id)
        .reduce((sum, s) => sum + Number(s.goals || 0), 0);
    const awayScorerGoals = scorers
        .filter((s) => s.team_id === match?.away_team_id)
        .reduce((sum, s) => sum + Number(s.goals || 0), 0);
    // Autogol a favore: pro casa = subìto dalla trasferta, e viceversa.
    const ownForHome = ownGoals.filter((o) => o.team_id === match?.away_team_id).length;
    const ownForAway = ownGoals.filter((o) => o.team_id === match?.home_team_id).length;
    const homeTotalGoals = homeScorerGoals + ownForHome;
    const awayTotalGoals = awayScorerGoals + ownForAway;
    const homeMatch = homeTotalGoals === Number(homeScore);
    const awayMatch = awayTotalGoals === Number(awayScore);

    // Giocatori schierati che sono SQUALIFICATI o INFORTUNATI → bloccano il salvataggio.
    const fieldedBlockedNames = useMemo(() => {
        const fielded = new Set();
        scorers.forEach((s) => s.player_pes_id && fielded.add(String(s.player_pes_id)));
        bookings.forEach((b) => b.player_pes_id && fielded.add(String(b.player_pes_id)));
        ownGoals.forEach((o) => o.player_pes_id && fielded.add(String(o.player_pes_id)));
        if (mvpId) { const p = mvpId.split(":")[1]; if (p) fielded.add(String(p)); }
        if (homeGk.pes_id) fielded.add(String(homeGk.pes_id));
        if (awayGk.pes_id) fielded.add(String(awayGk.pes_id));
        const names = [];
        fielded.forEach((pid) => {
            if (playerBlockReason(pid)) {
                const nm = allPlayers.find((p) => String(p.pes_id) === pid)?.name || pid;
                names.push(nm);
            }
        });
        return names;
    }, [scorers, bookings, ownGoals, mvpId, homeGk, awayGk, suspended, injured, allPlayers]);
    // L'admin è ESENTE dal blocco (corregge partite già giocate).
    const hasFieldedBlocked = fieldedBlockedNames.length > 0 && user?.role !== "admin";

    const submit = async () => {
        setError(null);
        if (!homeMatch || !awayMatch) {
            setError(`Totale gol (marcatori + autogol) non coincide col punteggio (casa ${homeTotalGoals}/${homeScore}, trasferta ${awayTotalGoals}/${awayScore})`);
            return;
        }
        // Validate scorers have a player selected
        for (const s of scorers) {
            if (!s.player_pes_id) { setError("Seleziona un marcatore per ogni riga"); return; }
        }
        // Validate bookings have a player selected
        for (const b of bookings) {
            if (!b.player_pes_id) { setError("Seleziona un giocatore per ogni cartellino"); return; }
        }
        // BLOCCO: non si può salvare se schieri un giocatore SQUALIFICATO o INFORTUNATO
        // (marcatore, ammonito/espulso, MVP o portiere). I diffidati possono giocare.
        // L'admin è ESENTE: corregge partite già giocate e deve poter cambiare qualunque cosa.
        if (hasFieldedBlocked) {
            const multi = fieldedBlockedNames.length > 1;
            setError(
                `🚫 Non puoi salvare: ${fieldedBlockedNames.join(", ")} ` +
                `${multi ? "sono squalificati/infortunati" : "è squalificato o infortunato"} ` +
                "e non può scendere in campo. Rimuovilo dal tabellino.",
            );
            return;
        }
        // BELLA (spareggio): se finisce in parità serve il vincitore ai rigori
        if (match?.leg === "replay" && Number(homeScore) === Number(awayScore) && !penaltyWinner) {
            setError("La 'bella' è in parità: indica chi ha vinto ai rigori.");
            return;
        }
        setSaving(true);
        try {
            let mvpPayload = null;
            if (mvpId) {
                const [tid, pid] = mvpId.split(":");
                mvpPayload = { team_id: tid, player_pes_id: pid };
            }
            const body = {
                home_score: Number(homeScore),
                away_score: Number(awayScore),
                scorers: scorers.map((s) => ({
                    player_pes_id: s.player_pes_id,
                    team_id: s.team_id,
                    goals: Number(s.goals),
                })),
                mvp: mvpPayload,
                own_goals: ownGoals.map((o) => ({
                    team_id: o.team_id,
                    player_pes_id: o.player_pes_id || null,
                })),
                home_gk: homeGk.pes_id ? { player_pes_id: homeGk.pes_id, rating: Number(homeGk.rating) } : null,
                away_gk: awayGk.pes_id ? { player_pes_id: awayGk.pes_id, rating: Number(awayGk.rating) } : null,
                bookings: bookings.map((b) => ({
                    player_pes_id: b.player_pes_id, team_id: b.team_id, type: b.type,
                })),
                penalty_winner: match?.leg === "replay" ? (penaltyWinner || null) : null,
            };
            const { data } = await api.put(`/matches/${matchId}/tabellino`, body);
            onSaved?.(data);
            onClose?.();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const resetTabellino = async () => {
        if (!confirm("Annullare il tabellino e riportare la partita a 'Da giocare'?")) return;
        setSaving(true);
        try {
            const { data } = await api.delete(`/matches/${matchId}/tabellino`);
            onSaved?.(data);
            onClose?.();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const setTavolino = async (winner) => {
        const side = winner === "home" ? match?.home_team?.name : match?.away_team?.name;
        if (!confirm(`Assegnare il TAVOLINO 3-0 a favore di ${side}?\nPrende i 3 punti in classifica, ma i gol NON si sommano.`)) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/matches/${matchId}/tavolino`, { winner });
            onSaved?.(data);
            onClose?.();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore tavolino");
        } finally { setSaving(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
            data-testid="tabellino-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="op-card w-full max-w-3xl my-8 mx-4">
                {loading ? (
                    <div className="p-10 op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
                ) : !match ? (
                    <div className="p-10 text-tff4d4d">{error || "Partita non trovata"}</div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="px-6 py-5 border-b border-t22252a flex items-center justify-between">
                            <div>
                                <div className="op-label text-[10px] text-tff7a1a">
                                    // GIORNATA {match.giornata} · {match.division_code}
                                </div>
                                <div className="op-heading text-tffffff text-2xl mt-1 flex items-center gap-3">
                                    <span>{match.home_team?.name}</span>
                                    <span className="text-t5c6370">vs</span>
                                    <span>{match.away_team?.name}</span>
                                </div>
                            </div>
                            <button onClick={onClose} className="op-btn op-btn-ghost text-xl" data-testid="tabellino-close">✕</button>
                        </div>

                        {!canEdit && (
                            <div className="px-6 py-3 bg-tff7a1a/10 border-b border-tff7a1a/30 text-tff7a1a text-xs op-label" data-testid="tabellino-readonly-banner">
                                {lockReason || "Sei in modalità lettura. Solo i presidenti delle 2 squadre o un admin possono editare."}
                            </div>
                        )}
                        {match?.auto_result && (
                            <div className="px-6 py-3 bg-tff7a1a/10 border-b border-tff7a1a/30 text-tff7a1a text-xs op-label" data-testid="tabellino-tavolino-banner">
                                Risultato assegnato d&apos;ufficio (tavolino). Per contestare, fai ricorso su Telegram.
                            </div>
                        )}

                        {match?.tavolino && (
                            <div className="px-6 py-3 bg-tff7a1a/10 border-b border-tff7a1a/30 text-tff7a1a text-xs op-label" data-testid="tabellino-tavolino-active-banner">
                                🏳️ TAVOLINO 3-0 · vince {match.tavolino_winner === "home" ? match.home_team?.name : match.away_team?.name} — punti sì, gol NON conteggiati.
                            </div>
                        )}

                        {match?.leg === "replay" && (
                            <div className="px-6 py-3 bg-tffd700/10 border-b border-tffd700/30 text-tffd700 text-xs op-label" data-testid="tabellino-bella-banner">
                                ⚔️ SPAREGGIO (LA BELLA) — confronto in parità dopo andata e ritorno. Vince chi passa il turno. In caso di pareggio, indica il vincitore ai rigori.
                            </div>
                        )}

                        {user?.role === "admin" && (
                            <div className="px-6 py-4 border-b border-t22252a bg-tff7a1a/[0.04]" data-testid="tavolino-admin-box">
                                <div className="op-label text-[10px] text-tff7a1a mb-2">🏳️ TAVOLINO (solo admin) — assegna 3-0 a tavolino</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button onClick={() => setTavolino("home")} disabled={saving} className="op-btn op-btn-ghost text-xs" data-testid="tavolino-home">
                                        Vince {match.home_team?.name} (3-0)
                                    </button>
                                    <button onClick={() => setTavolino("away")} disabled={saving} className="op-btn op-btn-ghost text-xs" data-testid="tavolino-away">
                                        Vince {match.away_team?.name} (0-3)
                                    </button>
                                    <span className="op-mono text-[10px] text-t5c6370">Il vincitore prende i 3 punti, i gol non si sommano in classifica.</span>
                                </div>
                            </div>
                        )}

                        {/* Suspended warning banner */}
                        {suspended.length > 0 && (
                            <div className="px-6 py-3 bg-tff4d4d/10 border-b border-tff4d4d/30" data-testid="suspended-banner">
                                <div className="op-label text-[10px] text-tff4d4d mb-1">⚠️ ATTENZIONE — SQUALIFICATI PER QUESTA PARTITA</div>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                                    {suspended.map((p) => (
                                        <span key={`${p.pes_id}-${p.team?.id || ""}`} className="text-tff4d4d">
                                            <span className="font-semibold">{p.name}</span>
                                            <span className="text-tc9ccd3"> ({p.team?.name})</span>
                                            <span className="text-t5c6370"> · {p.suspension_reason}</span>
                                        </span>
                                    ))}
                                </div>
                                <div className="op-mono text-[10px] text-t8f95a3 mt-1.5">
                                    Questi giocatori non dovrebbero essere schierati. Se li inserisci comunque il sistema te lo segnalerà.
                                </div>
                            </div>
                        )}

                        {/* Injured warning banner */}
                        {injured.length > 0 && (
                            <div className="px-6 py-3 bg-tff7a1a/10 border-b border-tff7a1a/30" data-testid="injured-banner">
                                <div className="op-label text-[10px] text-tff7a1a mb-1">🩹 ATTENZIONE — INFORTUNATI</div>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                                    {injured.map((p) => {
                                        const range = p.start_giornata && p.end_giornata
                                            ? (p.start_giornata === p.end_giornata
                                                ? `salta la giornata G${p.start_giornata}`
                                                : `salta le giornate G${p.start_giornata}–G${p.end_giornata}`)
                                            : `salta ancora ${p.weeks_remaining} ${p.weeks_remaining === 1 ? "partita" : "partite"}`;
                                        return (
                                            <span key={`${p.pes_id}-${p.team_id || ""}`} className="text-tff7a1a">
                                                <span className="font-semibold">{p.name}</span>
                                                <span className="text-t5c6370"> · {range}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                                <div className="op-mono text-[10px] text-t8f95a3 mt-1.5">
                                    L&apos;infortunio è agganciato a GIORNATE specifiche (campionato + coppe). Il giocatore rientra solo dopo quelle giornate, anche se una partita viene rinviata.
                                </div>
                            </div>
                        )}

                        {/* Score */}
                        <div className="px-6 py-6 border-b border-t22252a">
                            <div className="op-label text-[10px] text-t8f95a3 mb-3">PUNTEGGIO FINALE</div>
                            <div className="flex items-center justify-center gap-5">
                                <div className="text-right flex-1">
                                    <div className="text-tffffff text-sm">{match.home_team?.name}</div>
                                </div>
                                <input
                                    type="number" min="0" max="99"
                                    disabled={!canEdit}
                                    value={homeScore}
                                    onChange={(e) => setHomeScore(e.target.value)}
                                    className="w-20 text-center bg-t0e1014 border border-t22252a text-tffffff text-4xl py-2 op-mono focus:border-t2bb6ff focus:outline-none disabled:opacity-50"
                                    data-testid="home-score-input"
                                />
                                <span className="text-t5c6370 text-3xl">-</span>
                                <input
                                    type="number" min="0" max="99"
                                    disabled={!canEdit}
                                    value={awayScore}
                                    onChange={(e) => setAwayScore(e.target.value)}
                                    className="w-20 text-center bg-t0e1014 border border-t22252a text-tffffff text-4xl py-2 op-mono focus:border-t2bb6ff focus:outline-none disabled:opacity-50"
                                    data-testid="away-score-input"
                                />
                                <div className="text-left flex-1">
                                    <div className="text-tffffff text-sm">{match.away_team?.name}</div>
                                </div>
                            </div>

                            {/* BELLA: vincitore ai rigori (solo se pareggio) */}
                            {match.leg === "replay" && Number(homeScore) === Number(awayScore) && (
                                <div className="mt-5 pt-4 border-t border-t22252a" data-testid="penalty-winner-section">
                                    <div className="op-label text-[10px] text-tffd700 mb-3 text-center">
                                        🥅 PAREGGIO — VITTORIA AI RIGORI (chi passa il turno?)
                                    </div>
                                    <div className="flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            disabled={!canEdit}
                                            onClick={() => setPenaltyWinner(penaltyWinner === "home" ? "" : "home")}
                                            className={`op-btn text-xs px-4 py-2 disabled:opacity-50 ${penaltyWinner === "home" ? "op-btn-primary" : "op-btn-ghost"}`}
                                            data-testid="penalty-winner-home"
                                        >
                                            {penaltyWinner === "home" ? "☑" : "☐"} Rigori: {match.home_team?.name}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!canEdit}
                                            onClick={() => setPenaltyWinner(penaltyWinner === "away" ? "" : "away")}
                                            className={`op-btn text-xs px-4 py-2 disabled:opacity-50 ${penaltyWinner === "away" ? "op-btn-primary" : "op-btn-ghost"}`}
                                            data-testid="penalty-winner-away"
                                        >
                                            {penaltyWinner === "away" ? "☑" : "☐"} Rigori: {match.away_team?.name}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Scorers */}
                        <div className="px-6 py-5 border-b border-t22252a">
                            <div className="flex items-center justify-between mb-3">
                                <div className="op-label text-[10px] text-tff7a1a">⚽ MARCATORI</div>
                                <div className={`op-mono text-xs ${homeMatch && awayMatch ? "text-t7ce38b" : "text-tff4d4d"}`}>
                                    {homeTotalGoals}/{homeScore} · {awayTotalGoals}/{awayScore}
                                </div>
                            </div>
                            <div className="space-y-2">
                                {scorers.map((s, idx) => {
                                    const isHome = s.team_id === match.home_team_id;
                                    const list = isHome ? homeFieldPlayers : awayFieldPlayers;
                                    const blockReason = playerBlockReason(s.player_pes_id);
                                    return (
                                        <div key={idx} data-testid={`scorer-row-${idx}`}>
                                            <div className="flex gap-2 items-center">
                                                <span className={`op-label text-[10px] px-2 py-1 border min-w-[60px] text-center ${isHome ? "border-t2bb6ff text-t2bb6ff" : "border-tff7a1a text-tff7a1a"}`}>
                                                    {isHome ? "CASA" : "TRASF"}
                                                </span>
                                                <select
                                                    disabled={!canEdit}
                                                    value={s.player_pes_id}
                                                    onChange={(e) => updateScorer(idx, { player_pes_id: e.target.value })}
                                                    className="flex-1 bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 focus:border-t2bb6ff focus:outline-none disabled:opacity-50"
                                                    data-testid={`scorer-player-${idx}`}
                                                >
                                                    <option value="">-- giocatore --</option>
                                                    {list.map((p) => (
                                                        <option key={`${p.pes_id}-${p.name}-${p.position}`} value={p.pes_id}>{p.name} ({trPositionShort(p.position)})</option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="number" min="1" max="20"
                                                    disabled={!canEdit}
                                                    value={s.goals}
                                                    onChange={(e) => updateScorer(idx, { goals: e.target.value })}
                                                    className="w-16 text-center bg-t0e1014 border border-t22252a text-tffffff text-sm py-1.5 focus:border-t2bb6ff focus:outline-none disabled:opacity-50"
                                                    data-testid={`scorer-goals-${idx}`}
                                                />
                                                {canEdit && (
                                                    <button onClick={() => removeScorer(idx)} className="op-btn op-btn-ghost text-xs px-2" data-testid={`scorer-remove-${idx}`}>✕</button>
                                                )}
                                            </div>
                                            {blockReason && (
                                                <div className="op-mono text-[10px] text-tff4d4d ml-[68px] mt-1" data-testid={`scorer-susp-${idx}`}>
                                                    ⚠️ {blockReason} — non dovrebbe giocare
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {canEdit && (
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => addScorer(match.home_team_id)} className="op-btn op-btn-ghost text-xs" data-testid="add-home-scorer">+ Marc. Casa</button>
                                    <button onClick={() => addScorer(match.away_team_id)} className="op-btn op-btn-ghost text-xs" data-testid="add-away-scorer">+ Marc. Trasf.</button>
                                </div>
                            )}
                        </div>

                        {/* Own goals (autogol) */}
                        <div className="px-6 py-5 border-b border-t22252a">
                            <div className="flex items-center justify-between mb-3">
                                <div className="op-label text-[10px] text-tff7a1a">🥅 AUTOGOL</div>
                                <div className="op-mono text-[10px] text-t5c6370">
                                    {ownGoals.length} autogol
                                </div>
                            </div>
                            <div className="space-y-2">
                                {ownGoals.map((o, idx) => {
                                    // team_id = squadra di chi segna l'autogol (chi subisce); il gol va all'avversaria
                                    const concedingIsHome = o.team_id === match.home_team_id;
                                    const list = concedingIsHome ? homeFieldPlayers.concat(homeGks) : awayFieldPlayers.concat(awayGks);
                                    const benefLabel = concedingIsHome ? "pro Trasf." : "pro Casa";
                                    const blockReason = playerBlockReason(o.player_pes_id);
                                    return (
                                        <div key={idx} data-testid={`owngoal-row-${idx}`}>
                                            <div className="flex gap-2 items-center">
                                                <span className={`op-label text-[10px] px-2 py-1 border min-w-[78px] text-center ${concedingIsHome ? "border-tff7a1a text-tff7a1a" : "border-t2bb6ff text-t2bb6ff"}`}>
                                                    {benefLabel}
                                                </span>
                                                <select
                                                    disabled={!canEdit}
                                                    value={o.player_pes_id}
                                                    onChange={(e) => updateOwnGoal(idx, { player_pes_id: e.target.value })}
                                                    className="flex-1 bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 focus:border-tff7a1a focus:outline-none disabled:opacity-50"
                                                    data-testid={`owngoal-player-${idx}`}
                                                >
                                                    <option value="">-- autogol generico --</option>
                                                    {list.map((p) => (
                                                        <option key={`${p.pes_id}-${p.name}-${p.position}`} value={p.pes_id}>{p.name} ({trPositionShort(p.position)})</option>
                                                    ))}
                                                </select>
                                                {canEdit && (
                                                    <button onClick={() => removeOwnGoal(idx)} className="op-btn op-btn-ghost text-xs px-2" data-testid={`owngoal-remove-${idx}`}>✕</button>
                                                )}
                                            </div>
                                            {blockReason && (
                                                <div className="op-mono text-[10px] text-tff4d4d ml-[86px] mt-1" data-testid={`owngoal-susp-${idx}`}>
                                                    ⚠️ {blockReason} — non dovrebbe giocare
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {canEdit && (
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => addOwnGoal(match.away_team_id)} className="op-btn op-btn-ghost text-xs" data-testid="add-owngoal-home">+ Autogol pro Casa</button>
                                    <button onClick={() => addOwnGoal(match.home_team_id)} className="op-btn op-btn-ghost text-xs" data-testid="add-owngoal-away">+ Autogol pro Trasf.</button>
                                </div>
                            )}
                            <div className="op-mono text-[10px] text-t5c6370 mt-2">
                                L&apos;autogol può essere generico oppure attribuito al giocatore (di qualsiasi ruolo) che lo ha segnato. Il gol conta per la squadra avversaria.
                            </div>
                        </div>

                        {/* MVP */}
                        <div className="px-6 py-5 border-b border-t22252a">
                            <div className="op-label text-[10px] text-te8ff00 mb-3">⭐ MVP DELLA PARTITA</div>
                            <select
                                disabled={!canEdit}
                                value={mvpId}
                                onChange={(e) => setMvpId(e.target.value)}
                                className="w-full bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-2 focus:border-te8ff00 focus:outline-none disabled:opacity-50"
                                data-testid="mvp-select"
                            >
                                <option value="">-- nessun MVP --</option>
                                <optgroup label={`${match.home_team?.name} (Casa)`}>
                                    {homeRoster.map((p) => (
                                        <option key={`h-${p.pes_id}-${p.name}`} value={`${match.home_team_id}:${p.pes_id}`}>
                                            {p.name} ({trPositionShort(p.position)})
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label={`${match.away_team?.name} (Trasferta)`}>
                                    {awayRoster.map((p) => (
                                        <option key={`a-${p.pes_id}-${p.name}`} value={`${match.away_team_id}:${p.pes_id}`}>
                                            {p.name} ({trPositionShort(p.position)})
                                        </option>
                                    ))}
                                </optgroup>
                            </select>
                            {(() => {
                                const pid = mvpId ? mvpId.split(":")[1] : null;
                                const reason = pid ? playerBlockReason(pid) : null;
                                return reason ? (
                                    <div className="op-mono text-[10px] text-tff4d4d mt-2" data-testid="mvp-susp">
                                        ⚠️ {reason} — non dovrebbe giocare
                                    </div>
                                ) : null;
                            })()}
                        </div>

                        {/* Bookings (Cards) */}
                        <div className="px-6 py-5 border-b border-t22252a">
                            <div className="flex items-center justify-between mb-3">
                                <div className="op-label text-[10px] text-tff4d4d">🟨🟥 CARTELLINI</div>
                                <div className="op-mono text-[10px] text-t5c6370">
                                    {bookings.filter(b => b.type === "yellow").length} gialli · {bookings.filter(b => b.type !== "yellow").length} rossi
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {[
                                    { side: "home", teamId: match.home_team_id, tag: "CASA", color: "rgb(var(--tc-2bb6ff))", list: homeRoster },
                                    { side: "away", teamId: match.away_team_id, tag: "TRASF", color: "rgb(var(--tc-ff7a1a))", list: awayRoster },
                                ].map((col) => {
                                    const rows = bookings
                                        .map((b, idx) => ({ b, idx }))
                                        .filter((x) => x.b.team_id === col.teamId);
                                    return (
                                        <div key={col.side} className="border border-t22252a p-3" data-testid={`bookings-col-${col.side}`}>
                                            <div className="op-label text-[10px] mb-2" style={{ color: col.color }}>{col.tag}</div>
                                            <div className="space-y-2">
                                                {rows.length === 0 ? (
                                                    <div className="op-mono text-[10px] text-t5c6370">Nessun cartellino.</div>
                                                ) : rows.map(({ b, idx }) => {
                                                    const c = BOOKING_COLORS[b.type] || BOOKING_COLORS.yellow;
                                                    const blockReason = playerBlockReason(b.player_pes_id);
                                                    return (
                                                        <div key={idx} data-testid={`booking-row-${idx}`}>
                                                            <div className="flex gap-2 items-center">
                                                                <span className="op-label text-[9px] px-1.5 py-1 border text-center shrink-0"
                                                                    style={{ borderColor: withAlpha(c.bg, "66"), color: c.bg }}>
                                                                    {c.label}
                                                                </span>
                                                                <select
                                                                    disabled={!canEdit}
                                                                    value={b.player_pes_id}
                                                                    onChange={(e) => updateBooking(idx, { player_pes_id: e.target.value })}
                                                                    className="flex-1 min-w-0 bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 focus:border-t2bb6ff focus:outline-none disabled:opacity-50"
                                                                    data-testid={`booking-player-${idx}`}
                                                                >
                                                                    <option value="">-- giocatore --</option>
                                                                    {sortByRole(col.list).map((p) => (
                                                                        <option key={`${p.pes_id}-${p.name}-${p.position}`} value={p.pes_id}>{p.name} ({trPositionShort(p.position)})</option>
                                                                    ))}
                                                                </select>
                                                                {canEdit && (
                                                                    <button onClick={() => removeBooking(idx)} className="op-btn op-btn-ghost text-xs px-2 shrink-0" data-testid={`booking-remove-${idx}`}>✕</button>
                                                                )}
                                                            </div>
                                                            {blockReason && (
                                                                <div className="op-mono text-[10px] text-tff4d4d mt-1" data-testid={`booking-susp-${idx}`}>
                                                                    ⚠️ {blockReason} — non dovrebbe giocare
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {canEdit && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                                    <button onClick={() => addBooking(match.home_team_id, "yellow")} className="op-btn op-btn-ghost text-xs" data-testid="add-yellow-home">+ Giallo Casa</button>
                                    <button onClick={() => addBooking(match.away_team_id, "yellow")} className="op-btn op-btn-ghost text-xs" data-testid="add-yellow-away">+ Giallo Trasf.</button>
                                    <button onClick={() => addBooking(match.home_team_id, "red_direct")} className="op-btn op-btn-ghost text-xs text-tff4d4d" data-testid="add-red-direct-home">+ Rosso Casa</button>
                                    <button onClick={() => addBooking(match.away_team_id, "red_direct")} className="op-btn op-btn-ghost text-xs text-tff4d4d" data-testid="add-red-direct-away">+ Rosso Trasf.</button>
                                    <button onClick={() => addBooking(match.home_team_id, "red_double")} className="op-btn op-btn-ghost text-xs text-tff7a1a" data-testid="add-red-double-home">+ Doppia Amm. Casa</button>
                                    <button onClick={() => addBooking(match.away_team_id, "red_double")} className="op-btn op-btn-ghost text-xs text-tff7a1a" data-testid="add-red-double-away">+ Doppia Amm. Trasf.</button>
                                </div>
                            )}
                        </div>

                        {/* Goalkeepers */}
                        <div className="px-6 py-5 border-b border-t22252a">
                            <div className="op-label text-[10px] text-t00bcd4 mb-3">🧤 VOTI PORTIERI</div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <GkBlock
                                    label={`${match.home_team?.name} (Casa)`}
                                    options={homeGks}
                                    value={homeGk}
                                    onChange={setHomeGk}
                                    disabled={!canEdit}
                                    testIdPrefix="home-gk"
                                    suspReason={playerBlockReason(homeGk.pes_id)}
                                />
                                <GkBlock
                                    label={`${match.away_team?.name} (Trasferta)`}
                                    options={awayGks}
                                    value={awayGk}
                                    onChange={setAwayGk}
                                    disabled={!canEdit}
                                    testIdPrefix="away-gk"
                                    suspReason={playerBlockReason(awayGk.pes_id)}
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="px-6 py-3 text-tff4d4d text-sm" data-testid="tabellino-error">{error}</div>
                        )}

                        {/* Footer */}
                        <div className="px-6 py-4 flex items-center justify-between gap-3">
                            <div className="text-[10px] text-t5c6370 op-mono">
                                {match.inserted_by ? `Inserito da ${match.inserted_by}` : "Mai compilato"}
                                {match.last_edited_by && match.last_edited_by !== match.inserted_by ? ` · ultima modifica: ${match.last_edited_by}` : ""}
                            </div>
                            <div className="flex gap-2">
                                {user?.role === "admin" && match.status === "played" && (
                                    <button onClick={resetTabellino} disabled={saving} className="op-btn op-btn-ghost text-xs text-tff4d4d" data-testid="tabellino-reset">
                                        Reset
                                    </button>
                                )}
                                <button onClick={onClose} className="op-btn op-btn-ghost text-sm" data-testid="tabellino-cancel">
                                    {canEdit ? "Annulla" : "Chiudi"}
                                </button>
                                {canEdit && (
                                    <button
                                        onClick={submit}
                                        disabled={saving || !homeMatch || !awayMatch || hasFieldedBlocked}
                                        className={`op-btn text-sm ${hasFieldedBlocked ? "op-btn-ghost text-t5c6370 cursor-not-allowed" : "op-btn-primary"}`}
                                        title={hasFieldedBlocked ? `Non puoi salvare: ${fieldedBlockedNames.join(", ")} squalificato/infortunato in campo` : undefined}
                                        data-testid="tabellino-save"
                                    >
                                        {saving ? "Salvataggio…" : (hasFieldedBlocked ? "Salvataggio bloccato" : "Salva tabellino")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function GkBlock({ label, options, value, onChange, disabled, testIdPrefix, suspReason }) {
    return (
        <div className="op-card p-3" style={{ borderColor: "rgb(var(--tc-00bcd4) / 0.333)" }}>
            <div className="text-tffffff text-xs mb-2">{label}</div>
            <select
                disabled={disabled}
                value={value.pes_id}
                onChange={(e) => onChange({ ...value, pes_id: e.target.value })}
                className="w-full bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 focus:border-t00bcd4 focus:outline-none disabled:opacity-50 mb-2"
                data-testid={`${testIdPrefix}-player`}
            >
                <option value="">-- portiere --</option>
                {options.map((p) => (
                    <option key={`${p.pes_id}-${p.name}`} value={p.pes_id}>{p.name}</option>
                ))}
            </select>
            {suspReason && (
                <div className="op-mono text-[10px] text-tff4d4d mb-2" data-testid={`${testIdPrefix}-susp-warning`}>
                    ⚠️ Squalificato ({suspReason})
                </div>
            )}
            <div className="flex items-center gap-2">
                <label className="op-label text-[10px] text-t8f95a3">Voto</label>
                <input
                    type="number" min="0" max="10" step="0.5"
                    disabled={disabled}
                    value={value.rating}
                    onChange={(e) => onChange({ ...value, rating: e.target.value })}
                    className="w-20 bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 text-center focus:border-t00bcd4 focus:outline-none disabled:opacity-50"
                    data-testid={`${testIdPrefix}-rating`}
                />
            </div>
        </div>
    );
}
